/* @flow */

import * as React from 'react';
import * as ramda from 'ramda';
import classnames from 'classnames';
import {measureScrollbarWidth} from '../utils/scrollbar';
import './list-view.css';

type SpecialState = {
    shouldRender: () => boolean,
    component: React.Node,
};

type Props<M> = {|
    collection: M[],
    specialStates: SpecialState[],
    isLoading?: boolean,
    isTable?: boolean,
    shouldfadeBottom?: boolean,
    renderRow: (item: M, index: number) => React.Node,
    header?: React.Node,
    footer?: React.Node,
    getListViewRef?: (?HTMLElement) => void,
|};

const findSpecialState = ramda.find((state: SpecialState) => {
    return state.shouldRender();
});

/**
This is a generic, building-block component used to render a list of
items.  An array of items is specified as a `collection`, and each
item in the array is mapped into a `renderRow` function.  Additionally,
header and footer elements can also be provided, if desired, and they
will be added to the start and end of the list, respectively.

Finally, an array of `specialStates` can be provided.  These must
be objects with both a `shouldRender` function that returns true
or false, and a `component` prop which is a react element to display
if the `shouldRender` evaluates to `true`.
 */
export class ListView<M> extends React.Component<Props<M>, void> {
    scrollbarWidth: number;
    static defaultProps = {
        collection: [],
        specialStates: [],
    };

    constructor() {
        super();
        // This is just being used to detect if it's non-zero.
        // I don't have a great way to pass the actual value to an `::after` pseudoelement
        this.scrollbarWidth = measureScrollbarWidth();
    }

    render() {
        const Node = this.props.isTable ? 'table' : 'div';

        const styleNames = classnames({
            'list-view-loading': this.props.isLoading,
            'fade-full': this.props.shouldfadeBottom && !this.scrollbarWidth,
            'fade-with-scrollbar': this.props.shouldfadeBottom && this.scrollbarWidth,
        });

        return (
            <Node ref={this.props.getListViewRef} styleName={styleNames}>
                {this.renderBody()}
            </Node>
        );
    }

    renderBody = () => {
        const specialState = findSpecialState(this.props.specialStates);
        const header = this.props.isTable ? <thead>{this.props.header}</thead> : this.props.header;
        const list = this.props.isTable ? (
            <tbody>{this.props.collection.map(this.props.renderRow)}</tbody>
        ) : (
            <ul>{this.props.collection.map(this.props.renderRow)}</ul>
        );
        const footer = this.props.isTable ? <tfoot>{this.props.footer}</tfoot> : this.props.footer;

        return (
            <React.Fragment>
                {this.props.header ? header : null}
                {specialState ? specialState.component : list}
                {this.props.footer ? footer : null}
            </React.Fragment>
        );
    };
}
